<template>
  <liste-template>
    <template #header>
      <h1 class="title is-size-3">{{ $t('listeLigneCommandeAchat.title') }}</h1>
      <breadcrumb :breadcrumb="breadcrumb"/>
    </template>
    <template #bar-left>
      <form @submit.prevent="recherche()" class="column is-narrow">
        <b-input
          v-model="criteresRecherche.auto"
          :placeholder="$t('general.button.search')"
          type="search"
          icon="magnify">
        </b-input>
      </form>
      <div class='column is-narrow'>
        <button class="button" @click='changeValueShowFiltreAndLockMenu'>
          <b-icon icon="filter-variant" size="is-small" type="is-default"></b-icon>
          <span class="is-size-7">
            {{$t('general.button.filtre')}}
            <label class="has-text-weight-bold"
              :class="nombreCritere > 0 ? '' : 'is-invisible'">
              ({{ nombreCritere }})
            </label>
          </span>
        </button>
        <span @click="reinitialiser()"
          class="info-circle-right is-pulled-right is-cursor-pointer">
          <b-icon icon="close-circle" type="is-danger"></b-icon>
        </span>
      </div>
    </template>
    <template #bar-right>
      <div class="column is-narrow">
        <exportExcelButton
          :perm="permCommande.exporter"
          :query="exportExcelQuery"/>
      </div>
      <parametrage-table
        :reference-table="referenceTable"
        @endLoadParamtre="startArrayRendering = true">
      </parametrage-table>
    </template>
    <template #content>
      <v-server-table
        v-if="startArrayRendering" url=""
        :columns="columns"
        :options="options"
        :ref="referenceTable"
        @loaded="finLoading"
        @loading="debutLoading">
        <template #appendBody>
          <table-footer-totaux
            :columns="columns"
            :target="targetTotal"
            :items="lignesCommande">
          </table-footer-totaux>
        </template>
        <template #afterTable>
          <table-items-in-view
            :perPage="resultPerPage"
            @input="$refs[referenceTable].setLimit($event)"/>
        </template>
        <template #codeDcf="{ row }">
          <router-link-blank
            :to="{ name: 'c.achat.commandes', params: { id: row.devisCommandeId } }"
            :message="row.codeDcf">
          </router-link-blank>
        </template>
        <template #libelle="{ row }">
          {{ row.libelle }}
        </template>
        <template #produitLibelle="{ row }">
          {{ row.produitLibelle }}
        </template>
        <template #dateDevisCommande="{ row }">
          {{ func.formatDate(row.dateDevisCommande) }}
        </template>
        <template #listeIntervenants="{ row }">
          {{ row.listeIntervenants}}
        </template>
        <template #etatDcf="{ row }">
          {{ getEtatLibelle(row.etatDcf) }}
        </template>
        <template #quantite="{ row }">
          {{ row.quantite }}
        </template>
        <template #uniteVente="{ row }">
          {{ getUniteLibelle(row.uniteVente) }}
        </template>
        <template #totalHtAvecLigneRemise="{ row }">
          <div class="is-pulled-right">
            {{ func.formatDevise(row.totalHtAvecLigneRemise) }}
          </div>
        </template>
        <template #edit="{ row }">
          <router-link-blank
            :to="{ name: 'm.achat.commandes',  params: { id: row.devisCommandeId }}"
            icon="pencil-box-outline"
            typeIcon="is-success">
          </router-link-blank>
        </template>
      </v-server-table>
    </template>
    <template #filter>
      <div class="componentBrief" v-show="showFiltre">
        <filtre-lignes-commande-achat  ref="filtreListeLigneCommandeAchat"
          @toggleFiltre="changeValueShowFiltreAndLockMenu"
          @recherche="makeRechercheFilter"/>
      </div>
    </template>
  </liste-template>
</template>

<script>

import { createNamespacedHelpers } from 'vuex';
import cloneDeep from 'lodash/cloneDeep';
import listeTemplate from '@/components/general/template/listeTemplate';
import {
  NO_PER_PAGE_VALUE,
  SORT_ICON,
  RESULTS_PER_PAGE,
  TEXTS,
} from '@/configuration/clientTableConf';
import exportExcelButton from '@/components/general/button/exportExcelButton';
import { TYPE_COMMANDE, COMMANDE_ACHAT_FOURNISSEUR_ETAT, LISTE_UNITES } from '@/configuration/devisConf';
import EXPORT_EXCEL_LIGNE_COMMANDE_ACHAT from '@/configuration/achat/commandeAchat/commandeAchatConf';
import { SET_CRITERE } from '@/store/modules/achat/ligneCommandeAchat-mutation-type';
import formatDate from '@/services/general/formatDate';
import messageErreur from '@/services/general/messageErreur';
import { formatDevise } from '@/services/general/filters/formatDevise';
import { SET_DETECT_CHANGE_SOCIETE, SET_LOCK_MENU } from '@/store/modules/societe-mutation-type';
import { REFERENCE_TABLE_LIGNES_COMMANDES_ACHAT } from '@/configuration/general/referenceTableConf';
import routerLinkBlank from '@/components/general/routerLinkBlank';
import tableFooterTotaux from '@/components/general/tableFooterTotaux';
import TableItemsInView from '@/components/general/TableItemsInView';
import calculNombreCritere from '@/services/general/calculNombreCritere';
import parametrageTable from '@/components/general/parametrageTable';
import subscribeMutation from '@/services/store/subscribeMutation';
import filtreLignesCommandeAchat from '@/components/achat/ligneCommandeAchat/filtreLignesCommandeAchat';
import checkPeriodeAndFormat from '@/services/dcf/checkPeriodeAndFormat';
import { COMMANDES } from '@/configuration/achat/permissionsConf';
import breadcrumb from '@/components/general/breadcrumb';
import tagInputToArray from '@/services/general/filters/tagInputToArray';

const LignesCommande = createNamespacedHelpers('ligneCommandeAchat');
const Societe = createNamespacedHelpers('societe');

export default {
  name: 'ListeLigneCommandeAchat',
  data() {
    return {
      showFiltre: false,
      startArrayRendering: false,
      mountedLoadingEnd: false,
      referenceTable: REFERENCE_TABLE_LIGNES_COMMANDES_ACHAT,
      func: {
        formatDate,
        formatDevise,
      },
      resultPerPage: RESULTS_PER_PAGE,
      permCommande: COMMANDES,
    };
  },
  components: {
    exportExcelButton,
    routerLinkBlank,
    tableFooterTotaux,
    parametrageTable,
    TableItemsInView,
    filtreLignesCommandeAchat,
    listeTemplate,
    breadcrumb,
  },
  computed: {
    ...LignesCommande.mapGetters({
      lignesCommande: 'items',
      total: 'totalCount',
      criteresRecherche: 'criteresRecherche',
      lastPayload: 'lastPayload',
    }),
    ...Societe.mapGetters({
      societeActiveId: 'societeActiveId',
      lockMenu: 'lockMenu',
    }),
    nombreCritere() {
      const banCritere = ['auto', 'dateFacture', 'sens', 'typesDevisCommandeFacture', 'module',
        'sousTypesDevisCommandeFacture',
      ];
      let nbCritere = calculNombreCritere(this.criteresRecherche, banCritere);
      if (this.criteresRecherche.dateFacture) {
        nbCritere += this.criteresRecherche.dateFacture.dateDebut ? 1 : 0;
        nbCritere += this.criteresRecherche.dateFacture.dateFin ? 1 : 0;
      }
      return nbCritere > 9 ? '+9' : nbCritere;
    },
    targetTotal() {
      return ['totalHtAvecLigneRemise'];
    },
    breadcrumb() {
      return [
        { title: this.$i18n.t('general.breadcrumb.racine'), route: this.$PAGE_DEFAULT, isActive: false },
        { title: this.$i18n.t('listeLigneCommandeAchat.title'), route: { name: 'l.achat.commandes-ligne_commande_achat' }, isActive: true },
      ];
    },
    columnsData() {
      return [
        { name: 'codeDcf', sortable: true },
        { name: 'libelle', sortable: true },
        { name: 'dateDevisCommande', sortable: true },
        { name: 'listeIntervenants', sortable: false },
        { name: 'etatDcf', sortable: true },
        { name: 'produitLibelle', sortable: true },
        { name: 'quantite', sortable: true },
        { name: 'uniteVente', sortable: true },
        { name: 'totalHtAvecLigneRemise', sortable: false },
        { name: 'edit', noTranslate: true },
      ];
    },
    columns() {
      return this.columnsData.map((column) => column.name);
    },
    options() {
      const headings = this.columnsData.reduce((acc, val) => {
        acc[val.name] = !val.noTranslate ? this.$t(`listeLigneCommandeAchat.tabs.${val.name}`) : '';
        return acc;
      }, {});
      return {
        sortIcon: SORT_ICON,
        headings,
        filterable: false,
        sortable: this.columnsData.filter((c) => c.sortable).map((c) => c.name),
        perPageValues: NO_PER_PAGE_VALUE,
        perPage: this.resultPerPage,
        texts: TEXTS,
        requestFunction: ((data) => {
          if (!data.query) data.query = cloneDeep(this.criteresRecherche);
          this.gestionDataLoadLignes(data);
          return this.loadLignesCommandeAchat(data);
        }),
        responseAdapter: (() => {
          return {
            data: this.lignesCommande,
            count: this.total,
          };
        }),
      };
    },
    exportExcelQuery() {
      return {
        typeDonnee: EXPORT_EXCEL_LIGNE_COMMANDE_ACHAT,
        typeExport: null,
        filtres: this.lastPayload,
      };
    },
  },
  methods: {
    ...Societe.mapMutations({
      setLockMenu: SET_LOCK_MENU,
    }),
    ...LignesCommande.mapActions({
      loadLignesCommandeAchat: 'loadLignesCommandeAchat',
    }),
    ...LignesCommande.mapMutations({
      setCritereRecherche: SET_CRITERE,
    }),
    async changeValueShowFiltreAndLockMenu() {
      this.showFiltre = !this.showFiltre;
      if (this.showFiltre) {
        await this.$refs.filtreListeLigneCommandeAchat.initializeFiltre();
      }
      this.setLockMenu(!this.lockMenu);
    },
    makeRechercheFilter() {
      if (this.showFiltre) this.changeValueShowFiltreAndLockMenu();
      this.recherche();
    },
    gestionDataLoadLignes(data) {
      checkPeriodeAndFormat(data.query, 'dateFacture');
      data.query.tiersId = tagInputToArray(this.criteresRecherche.tiersId, 'id');
      data.query.produitsIds = tagInputToArray(this.criteresRecherche.produitsIds, 'id');
      data.query.societeId = this.societeActiveId;

      return data;
    },
    recherche() {
      this.$refs[this.referenceTable].refresh();
      if (this.showFiltre) {
        this.showFiltre = !this.showFiltre;
      }
    },
    reinitialiser() {
      this.$refs.filtreListeLigneCommandeAchat.viderCritere();
      this.$refs.filtreListeLigneCommandeAchat.appliquerCritere();
      this.showFiltre = false;
    },
    debutLoading() {
      if (this.loadingComponent) this.loadingComponent.close();
      this.loadingComponent = this.$buefy.loading.open();
    },
    finLoading() {
      this.loadingComponent.close();
    },
    getEtatLibelle(etat) {
      return this.$t(COMMANDE_ACHAT_FOURNISSEUR_ETAT.find(st => st.code === etat).libelle);
    },
    getUniteLibelle(uniteVente) {
      return this.$t(LISTE_UNITES.find((unite) => {
        return unite.code === uniteVente;
      })?.libelleLight) ?? '';
    },
  },
  beforeDestroy() {
    if (this.lockMenu) this.setLockMenu(false);
    this.unsubscribeValue();
  },
  async mounted() {
    try {
      this.unsubscribeValue = await subscribeMutation(this.$store, 'societe/' + SET_DETECT_CHANGE_SOCIETE, this.recherche);
      const critere = cloneDeep(this.criteresRecherche);
      this.criteresRecherche.typesDevisCommandeFacture = [TYPE_COMMANDE];
      this.setCritereRecherche(critere);
      this.setInformationPage(this.$t('pageAchat.ligneCommandeAchat.liste'));
      this.mountedLoadingEnd = true;
    } catch (e) {
      messageErreur(this.$i18n);
      throw e;
    }
  },
};
</script>
